<template>
  <CollapsableSection
    data-test="technical"
    :title="t('technical_specification')"
    section-classes="w-full text-xs text-gray-800 border-b border-gray-200 border-solid last:border-0"
    title-classes="lg:text-xl"
    :is-open="isOpen"
    collapsable-padding="px-8 xl:px-12 py-4"
    collapsable-content-classes="px-8 xl:px-12 py-6"
    @update:open="(value) => emit('update:open', value)"
  >
    <div class="flex flex-col w-full xl:flex-row xl:gap-x-16">
      <table v-for="(section, index) in sections" :key="index" class="w-full max-w-[562px] mt-1 first:mt-0 xl:mt-0">
        <tbody>
          <tr
            v-for="attribute in section"
            :key="attribute.attributeName"
            class="flex justify-between pt-1 pr-0 border-b border-gray-300 border-solid first:pt-0"
          >
            <td>{{ attribute.attributeLabel || attribute.attributeName }}:</td>
            <td class="flex text-right">{{ getAttributeValue(attribute) }}</td>
          </tr>
          <tr v-if="brandName && index === brandColumn" data-test="brand-spec" class="flex justify-between pt-1 pr-0">
            <td>{{ t('brand') }}:</td>
            <td>
              <NuxtLink class="flex underline" :to="linkToBrandPage">
                {{ brandName }}
              </NuxtLink>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </CollapsableSection>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { ProductAttribute } from '@/types/products';

type Props = {
  isOpen?: boolean;
  theAttributes?: ProductAttribute[];
  brandName?: string;
};

const emit = defineEmits<{
  (e: 'update:open', v: boolean): void;
}>();

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  theAttributes: () => [],
  brandName: '',
});

const { t } = useI18n();
const { mq } = useMediaQueries();

const { getAttributeValue } = useProductAttributes();

const linkToBrandPage = computed(() => ({ name: 'search', query: { q: props.brandName, brand: props.brandName } }));
const isMultiSection = computed(() => {
  if (['xsmall', 'small', 'medium', 'large'].includes(mq.value?.current || '')) return false;

  return props.theAttributes.length > 7;
});
const sections = computed(() => {
  if (!isMultiSection.value) return [props.theAttributes];

  const theAttributes = props.theAttributes;
  const half = Math.ceil(theAttributes.length / 2);
  return [theAttributes.slice(0, half), theAttributes.slice(half)];
});

const brandColumn = computed(() => {
  if (sections.value.length === 1) return 0;
  const [first, second] = sections.value;
  return first.length > second.length ? 1 : 0;
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'ProductDetailPageTechnicalSection',
});
</script>
